import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, Plus, X, FlaskConical, Pencil, AlertTriangle, CheckCircle2, Archive, Lock, Wind } from 'lucide-react';
import SearchableDropdown from './SearchableDropdown';

function calcKebutuhan(persentase, ukuranMl, qty) {
  const val = String(persentase).replace(',', '.');
  const ml = ((parseFloat(val) || 0) / 100) * ukuranMl * qty;
  if (ml >= 1000) return { nilai: +(ml / 1000).toFixed(2), satuan: 'L' };
  return { nilai: +ml.toFixed(2), satuan: 'ml' };
}

export default function RacikanPOSection({ ukuranMl, qty, racikanList, setRacikanList, onAutoFillComponents, onProductNameChange, onUkuranChange }) {
  const [mode, setMode] = useState('db');
  const [manualBahan, setManualBahan] = useState({ nama_bahan: '', kategori_bahan: 'Bibit', persentase: '', vendor: '' });

  const [dbBrand, setDbBrand] = useState('');
  const [dbVarian, setDbVarian] = useState('');

  // Track pilihan aktif untuk cross-validation
  const [activeBrandName, setActiveBrandName] = useState('');
  const [activeVarianName, setActiveVarianName] = useState('');

  const { data: racikanDb = [] } = useQuery({
    queryKey: ['racikan-database'],
    queryFn: () => base44.entities.RacikanDatabase.list('nama_brand'),
  });
  const { data: bahanDb = [] } = useQuery({
    queryKey: ['database-bahan'],
    queryFn: () => base44.entities.DatabaseBahan.list('nama_brand'),
  });

  const brandsData = useMemo(() => {
    const map = {};
    racikanDb.forEach(r => {
      const bName = r.nama_brand || 'No Brand';
      if (!map[bName]) map[bName] = { name: bName, variants: {} };
      const vName = r.nama_varian || 'Unnamed';
      if (!map[bName].variants[vName]) {
        map[bName].variants[vName] = { name: vName, racikan: null, bahan: null };
      }
      map[bName].variants[vName].racikan = r;
    });

    bahanDb.forEach(b => {
      const bName = b.nama_brand || 'No Brand';
      if (!map[bName]) map[bName] = { name: bName, variants: {} };
      const vName = b.nama_varian || 'Unnamed';
      if (!map[bName].variants[vName]) {
        map[bName].variants[vName] = { name: vName, racikan: null, bahan: null };
      }
      map[bName].variants[vName].bahan = b;
    });

    return Object.values(map).map(brand => ({
      ...brand,
      variants: Object.values(brand.variants)
    })).sort((a, b) => a.name.localeCompare(b.name, 'id'));
  }, [racikanDb, bahanDb]);

  const activeBrandData = useMemo(() => {
    if (!dbBrand) return null;
    return brandsData.find(b => b.name === dbBrand) || null;
  }, [brandsData, dbBrand]);

  const activeVarianData = useMemo(() => {
    if (!activeBrandData || !dbVarian) return null;
    return activeBrandData.variants.find(v => v.name === dbVarian) || null;
  }, [activeBrandData, dbVarian]);

  const loadFromDatabaseBrand = () => {
    if (!activeVarianData) return;
    
    // Load Racikan
    let mappedRacikan = [];
    if (activeVarianData.racikan) {
      mappedRacikan = (activeVarianData.racikan.racikan || []).map(r => ({ ...r, _source: 'db' }));
    } else if (activeVarianData.bahan && activeVarianData.bahan.racikan) {
      // Fallback
      mappedRacikan = (activeVarianData.bahan.racikan || []).map(r => ({
        nama_bahan: r.nama_bahan || r.nama,
        kategori_bahan: r.kategori_bahan || r.kategori || 'Lainnya',
        persentase: r.persentase || 0,
        vendor: r.vendor || '',
        _source: 'bahandb',
      }));
    }
    
    setRacikanList(mappedRacikan);
    setActiveBrandName(dbBrand);
    setActiveVarianName(dbVarian);

    // Call autofill
    if (onAutoFillComponents) onAutoFillComponents(activeVarianData.bahan || null, mappedRacikan);
    
    // Auto fill nama
    if (onProductNameChange && dbVarian) {
      onProductNameChange(dbVarian);
    }
    
    // Auto fill ukuran botol (prioritas dari Racikan, lalu dari Bahan)
    if (onUkuranChange) {
      if (activeVarianData.racikan && activeVarianData.racikan.ukuran_ml) {
        onUkuranChange(activeVarianData.racikan.ukuran_ml);
      } else if (activeVarianData.bahan && activeVarianData.bahan.botol_ukuran_label_ml) {
        onUkuranChange(activeVarianData.bahan.botol_ukuran_label_ml);
      }
    }
  };

  const addManual = () => {
    if (!manualBahan.nama_bahan || !manualBahan.persentase) return;
    setRacikanList(prev => [...prev, { ...manualBahan, _source: 'manual', persentase: Number(manualBahan.persentase) }]);
    setManualBahan({ nama_bahan: '', kategori_bahan: 'Bibit', persentase: '', vendor: '' });
  };

  const totalPersen = racikanList.reduce((s, r) => s + (parseFloat(String(r.persentase).replace(',', '.')) || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-purple-50 text-purple-700 border-purple-200">
        <FlaskConical className="w-4 h-4 shrink-0" />
        <span className="text-sm font-semibold">Racikan / Formula</span>
        <Badge variant="secondary" className="ml-auto text-xs">{racikanList.length} bahan</Badge>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'db', label: 'Database Brand', icon: BookOpen, active: 'bg-purple-100 text-purple-700 border-purple-300' },
          { key: 'manual', label: 'Input Manual', icon: Pencil, active: 'bg-amber-100 text-amber-700 border-amber-300' },
        ].map(({ key, label, icon: Icon, active }) => (
          <button key={key} type="button" onClick={() => setMode(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${mode === key ? active : 'border-border text-muted-foreground hover:bg-muted/50'}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* ===== Database Brand ===== */}
      {mode === 'db' && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 space-y-3">
          <p className="text-xs text-purple-700 font-medium">Pilih brand → varian → klik Muat untuk memuat formula & kemasan sekaligus.</p>

          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Brand</Label>
            <SearchableDropdown
              options={brandsData.map(b => ({ value: b.name, label: b.name }))}
              value={dbBrand}
              onChange={v => { setDbBrand(v); setDbVarian(''); }}
              placeholder="Pilih brand..." searchPlaceholder="Cari brand..."
            />
          </div>

          {activeBrandData && (
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Varian / Produk</Label>
              <SearchableDropdown
                options={activeBrandData.variants.map(v => ({
                  value: v.name, label: v.name,
                  sublabel: `${v.racikan ? 'Ada Formula' : 'Tanpa Formula'} · ${v.bahan ? 'Ada Kemasan' : 'Tanpa Kemasan'}`
                }))}
                value={dbVarian}
                onChange={setDbVarian}
                placeholder="Pilih varian..." searchPlaceholder="Cari varian..."
              />
            </div>
          )}

          {activeVarianData && (
            <div className="bg-white rounded-lg border border-purple-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-purple-700">{dbBrand} — {dbVarian}</p>
                  <p className="text-xs text-muted-foreground">
                    {activeVarianData.racikan?.ukuran_ml ? `${activeVarianData.racikan.ukuran_ml}ml` : ''} 
                    {activeVarianData.racikan ? ` · ${activeVarianData.racikan.racikan?.length || 0} bahan cairan` : ' · (Tanpa formula)'}
                    {activeVarianData.bahan ? ` · Packaging lengkap` : ' · (Tanpa packaging)'}
                  </p>
                </div>
                <Button type="button" size="sm" onClick={loadFromDatabaseBrand}
                  className="h-7 text-xs gap-1 bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-3 h-3" /> Muat Formula & Kemasan
                </Button>
              </div>

              {activeVarianData.bahan && (
                <div className="space-y-1 pb-1 pt-1 border-b border-purple-100">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase">Komponen Kemasan:</p>
                  {activeVarianData.bahan.botol_nama && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <Archive strokeWidth={1.5} className="w-3 h-3 text-[#8C6208]" /><span className="font-medium text-slate-700">{activeVarianData.bahan.botol_nama}</span>
                      {activeVarianData.bahan.botol_ukuran_aktual_ml && <span className="text-muted-foreground">(aktual {activeVarianData.bahan.botol_ukuran_aktual_ml}ml)</span>}
                    </div>
                  )}
                  {activeVarianData.bahan.tutup_nama && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <Lock strokeWidth={1.5} className="w-3 h-3 text-[#8C6208]" /><span className="font-medium text-slate-700">{activeVarianData.bahan.tutup_nama}</span>
                    </div>
                  )}
                  {activeVarianData.bahan.spray_nama && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <Wind strokeWidth={1.5} className="w-3 h-3 text-[#8C6208]" /><span className="font-medium text-slate-700">{activeVarianData.bahan.spray_nama}</span>
                    </div>
                  )}
                </div>
              )}

              {activeVarianData.racikan && (
                <div className="pt-1">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Formula Cairan:</p>
                  {(activeVarianData.racikan.racikan || []).map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px] py-0.5">
                      <span className="font-medium text-slate-700">{r.nama_bahan}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[9px] py-0">{r.kategori_bahan}</Badge>
                        <span className="font-bold text-purple-700 w-10 text-right">{r.persentase}%</span>
                      </div>
                    </div>
                  ))}
                  {activeVarianData.racikan.catatan && <p className="text-xs text-muted-foreground italic mt-2">📝 {activeVarianData.racikan.catatan}</p>}
                </div>
              )}
            </div>
          )}

          {brandsData.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">Belum ada data di Database Brand</p>}
        </div>
      )}

      {/* ===== Manual ===== */}
      {mode === 'manual' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
          <p className="text-xs font-semibold text-amber-700">Tambah Bahan Racikan Manual</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Nama Bahan *</Label>
              <Input className="h-8 text-xs" value={manualBahan.nama_bahan}
                onChange={e => setManualBahan(p => ({ ...p, nama_bahan: e.target.value }))} placeholder="Bibit Sun Zest..." />
            </div>
            <div>
              <Label className="text-xs">Kategori</Label>
              <Select value={manualBahan.kategori_bahan} onValueChange={v => setManualBahan(p => ({ ...p, kategori_bahan: v }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Bibit', 'Alkohol', 'DPG', 'Isopropil', 'Air', 'Pewarna', 'Lainnya'].map(k => (
                    <SelectItem key={k} value={k} className="text-xs">{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Vendor</Label>
              <Input className="h-8 text-xs" value={manualBahan.vendor}
                onChange={e => setManualBahan(p => ({ ...p, vendor: e.target.value }))} placeholder="Opsional..." />
            </div>
            <div>
              <Label className="text-xs">Persentase (%) *</Label>
              <Input type="number" min="0" max="100" step="0.1" className="h-8 text-xs"
                value={manualBahan.persentase}
                onChange={e => setManualBahan(p => ({ ...p, persentase: e.target.value }))} placeholder="30" />
            </div>
          </div>
          <Button type="button" size="sm" onClick={addManual}
            disabled={!manualBahan.nama_bahan || !manualBahan.persentase}
            className="w-full h-7 text-xs gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Tambah ke Racikan
          </Button>
        </div>
      )}

      {/* Racikan list */}
      {racikanList.length > 0 && (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="bg-muted/50 px-3 py-2 grid grid-cols-12 gap-1 text-xs font-semibold text-muted-foreground">
            <span className="col-span-4">Bahan</span>
            <span className="col-span-2 text-center">%</span>
            <span className="col-span-4 text-center">Kebutuhan</span>
            <span className="col-span-2"></span>
          </div>
          {racikanList.map((r, i) => {
            const kebutuhan = (ukuranMl > 0 && qty > 0) ? calcKebutuhan(r.persentase, ukuranMl, qty) : null;
            return (
              <div key={i} className="border-t border-border/50 px-3 py-2 grid grid-cols-12 gap-1 items-center hover:bg-muted/20">
                <div className="col-span-4">
                  <p className="text-xs font-medium leading-tight">{r.nama_bahan}</p>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    <Badge variant="outline" className="text-[10px] py-0">{r.kategori_bahan}</Badge>
                    {r.vendor && <span className="text-[10px] text-muted-foreground">{r.vendor}</span>}
                  </div>
                </div>
                <div className="col-span-2 text-center">
                  <span className="text-xs font-bold text-purple-700">{r.persentase}%</span>
                </div>
                <div className="col-span-4 text-center">
                  {kebutuhan
                    ? <span className="text-xs font-semibold text-blue-700">{kebutuhan.nilai} {kebutuhan.satuan}</span>
                    : <span className="text-xs text-muted-foreground">isi qty & ml</span>}
                </div>
                <div className="col-span-2 flex justify-end">
                  <button type="button" onClick={() => setRacikanList(prev => prev.filter((_, idx) => idx !== i))}
                    className="text-destructive/50 hover:text-destructive transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
          <div className="border-t border-border bg-muted/30 px-3 py-2 flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Total</span>
            <span className={`text-xs font-bold ml-2 ${Math.abs(totalPersen - 100) < 0.1 ? 'text-green-600' : 'text-amber-600'}`}>
              {totalPersen.toFixed(1)}%
            </span>
          </div>
        </div>
      )}

      {ukuranMl > 0 && qty > 0 && racikanList.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Kalkulasi Otomatis Aktif</p>
            <p>Botol <strong>{ukuranMl}ml</strong> × <strong>{qty} pcs</strong> = <strong>{(ukuranMl * qty / 1000).toFixed(2)} L</strong> total volume</p>
            {Math.abs(totalPersen - 100) > 0.1 && (
              <p className="mt-1 text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Total % belum 100% ({totalPersen.toFixed(1)}%)
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}